import { useQuery } from '@tanstack/react-query';
import type { Dispatch, SetStateAction } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getArtists } from '~/modules/artist-roster/lib/artists.ts';
import { getLabels } from '~/modules/artist-roster/lib/labels';
import type { LabelData } from '~/modules/artist-roster/types';
import { ICON_BY_TYPE } from '~/modules/link-farm/components/LinkList/link-item-icons';
import { useLinkFarmLinks } from '~/modules/link-farm/hooks/useLinkFarmLinks';
import type { LinkFarmLink } from '~/modules/link-farm/types';

import type {
  ArtistLinkItem,
  GlobalSearchResultItems,
  LabelLinkItem,
  LinkItem,
} from '../lib/types';

interface UseSearchProps {
  searchQuery: string;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
}

const INTERNAL_LINK_ICON = 'WARNER' as const;

export const useSearch = ({
  searchQuery,
  setIsLoading,
}: UseSearchProps): GlobalSearchResultItems => {
  const { t } = useTranslation('wmgone');
  const [tools, setTools] = useState<LinkItem[]>([]);
  const [artists, setArtists] = useState<ArtistLinkItem[]>([]);
  const [labels, setLabels] = useState<LabelLinkItem[]>([]);
  const { data } = useQuery({
    queryKey: ['artistAndLabelsSearch', searchQuery],
    queryFn: async () => {
      setIsLoading(true);

      const [artistsResult, labelsResult] = await Promise.all([
        getArtists({ search: searchQuery }),
        getLabels({}),
      ]);

      return { artistsResult, labelsResult };
    },
    enabled: searchQuery.length > 0,
  });

  const links = useLinkFarmLinks({
    linkType: 'all',
  });

  const linkFarmItems: LinkItem[] = useMemo(() => {
    return links.map((item: LinkFarmLink) => {
      if (item.isInternal) {
        return {
          id: item.url,
          icon: INTERNAL_LINK_ICON,
          url: item.url,
          actionText: t(item.descriptionKey),
          isInternal: item.isInternal,
          appName: item.appName,
        };
      }

      return {
        id: item.url,
        icon: item.icon,
        appName: item.appName,
        url: item.url,
        actionText: item.actionText,
        category: item.category,
        requestAccess: item.requestAccess,
        requireVpn: item.requireVpn,
        isInternal: item.isInternal,
      };
    });
  }, [links, t]);

  const itemsString = JSON.stringify(linkFarmItems);

  useEffect(() => {
    if (data?.labelsResult && searchQuery) {
      const matchedLabels = data.labelsResult.filter((label: LabelData) =>
        label.name.toLowerCase().includes(searchQuery.toLowerCase()),
      );

      setLabels(
        matchedLabels.map((label: LabelData) => ({
          icon: ICON_BY_TYPE.WARNER,
          id: label.labelId.toString(),
          appName: label.name,
          url: `roster?labels=${label.labelId}`,
          actionText: label.name,
          category: 'label',
          isInternal: true,
        })),
      );
    } else {
      setLabels([]);
    }

    setTools(
      searchQuery
        ? linkFarmItems.filter((item) =>
            [item.actionText, item.appName, item.url].some((field) =>
              field.toLowerCase().includes(searchQuery.toLowerCase()),
            ),
          )
        : [],
    );

    if (data?.artistsResult) {
      setArtists(
        data.artistsResult.items.map((artist) => ({
          icon: artist.image ?? ICON_BY_TYPE.WARNER,
          id: artist.partyId.toString(),
          appName: artist.name,
          url: `roster/${artist.partyId}`,
          actionText: artist.name,
          category: 'artist',
          requestAccess: 'false',
          requireVpn: false,
          isInternal: true,
        })),
      );
    } else {
      setArtists([]);
    }

    if (data) {
      setIsLoading(false);
    }
    // Disabling exhaustive-deps because itemsString covers changes in linkFarmItems
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, data, setIsLoading, itemsString]);

  return {
    tools,
    artists,
    tracks: [],
    labels,
  };
};
